import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { track } from '@/lib/analytics';
import { fetchApi } from '@/lib/api';
import { AtAGlance, Footer, SEO } from '@/components';
import { buildHarborSoftwareApplicationJsonLd, HARBOR_FACTS } from '@/lib/harbor-facts';
import { useIsDivorceProductEnabled } from '@/hooks/usePublicFeatureFlags';
import {
  getAppStoreUrl,
  getPlayStoreUrl,
  getUtmParamsFromUrl,
} from '@/lib/urls';
import {
  FiArrowLeft,
  FiDollarSign,
  FiMessageCircle,
  FiCalendar,
  FiFileText,
  FiSmartphone,
  FiBell,
  FiCheckCircle,
  FiMail,
  FiMessageSquare,
  FiTrendingDown,
  FiDownload,
  FiFolder,
  FiBookOpen,
} from 'react-icons/fi';
import { getPricingFeatures, type PricingFeature } from '@/lib/pricing-features';
import { FaApple, FaGooglePlay } from 'react-icons/fa';
import shipSvg from '@/assets/ship.svg';
import devicesPng from '@/assets/devices.png';
import laptopExamplePng from '@/assets/laptop-example.png';
import harborSs1Png from '@/assets/harbor_ss1.png';
import harborSsCalendarPng from '@/assets/harbor_ss_calendar.png';
import harborSsChatPng from '@/assets/harbor_ss_chat.png';
import harborSsJournalPng from '@/assets/harbor_ss_journal.png';
import harborSsExpenseReceiptPng from '@/assets/harbor_ss_expense_receipt.png';
import harborSsExportsPng from '@/assets/harbor_ss_exports.png';
import harborSsFileVaultPng from '@/assets/harbor_ss_file_vault.png';

// Declare global gtagSendEvent and fbq functions from index.html
declare global {
  interface Window {
    gtagSendEvent: (eventName: string) => void;
    fbq: (...args: unknown[]) => void;
  }
}

// Analytics handlers with Google Ads conversion tracking
const handleAppStoreClick = (location: string) => {
  track('Click App Store', { location });
  if (typeof window.gtagSendEvent === 'function') {
    window.gtagSendEvent('click_app_store');
  }
  if (typeof window.fbq === 'function') {
    window.fbq('trackCustom', 'ClickAppStore', { location });
  }
};

const handleGooglePlayClick = (location: string) => {
  track('Click Google Play', { location });
  if (typeof window.gtagSendEvent === 'function') {
    window.gtagSendEvent('click_google_play');
  }
  if (typeof window.fbq === 'function') {
    window.fbq('trackCustom', 'ClickGooglePlay', { location });
  }
};

const handleSignUpClick = (location: string) => {
  track('Click Sign Up', { location });
  if (typeof window.gtagSendEvent === 'function') {
    window.gtagSendEvent('click_sign_up');
  }
  if (typeof window.fbq === 'function') {
    window.fbq('track', 'Lead');
  }
};

const handleContactEmailClick = (location: string) => {
  track('Click Contact Email', { location });
};

const handleSupportSMSClick = (location: string) => {
  track('Click Support SMS', { location });
};

const handleFeatureTabClick = (tab: string) => {
  track('Click Feature Tab', { tab, audience: 'coparents' });
};

// Format phone number for display (e.g., +14157469059 -> (415) 746-9059)
const formatPhoneNumber = (phone: string): string => {
  // Remove +1 and any non-digits
  const digits = phone.replace(/\D/g, '').replace(/^1/, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  // Fallback to original if format is unexpected
  return phone;
};

// Support info response type
interface SupportInfoResponse {
  phone_number: string;
}

// Feature data for co-parents
const features = [
  {
    id: 'expenses',
    icon: FiDollarSign,
    title: 'Expense Tracking',
    shortTitle: 'Expenses',
    description:
      'Split child-related costs fairly with your co-parent. Log expenses as they happen, attach receipts, and see exactly who owes what at any time.',
    details: [
      'Log expenses in seconds with receipt photos',
      'Automatic 50/50 splits or custom percentages',
      'Running balance shows who owes whom',
      'Categories for medical, education, activities & more',
    ],
    image: harborSs1Png,
  },
  {
    id: 'calendar',
    icon: FiCalendar,
    title: 'Custody Calendar',
    shortTitle: 'Calendar',
    description:
      'Stay on the same page about parenting time. Set up your custody schedule once and both parents always know whose day it is.',
    details: [
      'Weekly rotation, 2-2-3, or custom schedules',
      'Add exceptions for holidays and special events',
      'Clear visual calendar for the whole family',
      'Never argue about whose weekend it is',
    ],
    image: harborSsCalendarPng,
  },
  {
    id: 'messaging',
    icon: FiMessageCircle,
    title: 'Secure Messaging',
    shortTitle: 'Messages',
    description:
      'Keep conversations documented and organized. All messages are timestamped and stored securely — no more "I never said that" arguments.',
    details: [
      'Tamper-proof message history',
      'Share photos and documents',
      'Everything timestamped for your records',
      'Keep communication in one organized place',
    ],
    image: harborSsChatPng,
  },
  {
    id: 'journal',
    icon: FiBookOpen,
    title: 'Private Journal',
    shortTitle: 'Journal',
    description:
      'A notebook just for you — not shared with your co-parent. Capture medical visits, custody handoffs, and personal notes with timestamps and categories so your own record stays organized.',
    details: [
      'Private — not visible to your co-parent',
      'Capture medical visits, handoffs, and personal notes',
      'Timestamps and categories for easy organization',
      'Build your own record for reference or court',
    ],
    image: harborSsJournalPng,
  },
  {
    id: 'records',
    icon: FiFileText,
    title: 'Court-Ready Records',
    shortTitle: 'Records',
    description:
      'Everything you need, ready when you need it. Export comprehensive reports of expenses, payments, and communications anytime.',
    details: [
      'PDF and CSV exports',
      'Complete expense and payment history',
      'Message records with timestamps',
      'Court-ready documentation you can export',
    ],
    image: harborSsExpenseReceiptPng,
  },
  {
    id: 'exports',
    icon: FiDownload,
    title: 'One-Tap Exports',
    shortTitle: 'Exports',
    description:
      'Export all your records in seconds. Select what you need — messages, expenses, or calendar — enter your email, and receive a comprehensive report instantly.',
    details: [
      'Export with just one tap',
      'Choose specific data: chat, expenses, or calendar',
      'Receive reports directly to your email',
      'Perfect for attorneys or court filings',
    ],
    image: harborSsExportsPng,
  },
  {
    id: 'multiplatform',
    icon: FiSmartphone,
    title: 'iOS, Android & Web',
    shortTitle: 'Apps',
    description:
      'Access Harbor anywhere, on any device. Use the mobile app on the go or the web app when you need a bigger screen.',
    details: [
      'Native iOS and Android apps',
      'Full-featured web app',
      'Synced across all your devices',
      'Works on phone, tablet, or computer',
    ],
    image: laptopExamplePng,
  },
  {
    id: 'notifications',
    icon: FiBell,
    title: 'Push Notifications',
    shortTitle: 'Alerts',
    description:
      'Never miss an update. Get notified instantly when your co-parent logs an expense, sends a message, or makes a payment.',
    details: [
      'Real-time notifications',
      'Customizable alert preferences',
      'Stay informed without constantly checking',
      'Quick actions right from notifications',
    ],
    image: devicesPng,
  },
  {
    id: 'vault',
    icon: FiFolder,
    title: 'Document Vault',
    shortTitle: 'Vault',
    description:
      'Store important documents and photos for each child in one secure place. Both parents can access and upload files, keeping everything organized and accessible.',
    details: [
      'Dedicated vault for each child',
      'Upload photos, documents, and files',
      'Both parents can view and upload',
      'Download and share files anytime',
    ],
    image: harborSsFileVaultPng,
  },
];

// Header Component
function LearnHeader() {
  return (
    <header className='fixed top-0 left-0 right-0 z-50 bg-primary/95 backdrop-blur-sm'>
      <nav className='max-w-7xl mx-auto px-6 py-4'>
        <div className='flex items-center justify-between'>
          <Link
            to='/'
            search={{ hero: undefined }}
            className='flex items-center gap-2 text-white text-2xl font-light tracking-tight leading-none'
            style={{
              fontFamily:
                "'Khula', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
            }}
          >
            <div className='flex items-center'>
              <img src={shipSvg} alt='' className='w-5 h-5 mr-3' />
              <span className='flex items-center mt-2'>Harbor</span>
            </div>
          </Link>
          <Link
            to='/'
            search={{ hero: undefined }}
            className='flex items-center gap-2 text-white/90 hover:text-white transition-colors font-medium'
            style={{
              fontFamily:
                "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
              fontWeight: 400,
            }}
          >
            <FiArrowLeft />
            Back to Home
          </Link>
        </div>
      </nav>
    </header>
  );
}

// Feature Tab Carousel
function FeatureTabCarousel({
  selectedFeature,
  onSelectFeature,
}: {
  selectedFeature: string;
  onSelectFeature: (id: string) => void;
}) {
  return (
    <div className='w-full overflow-x-auto scrollbar-hide'>
      <div className='flex gap-2 md:gap-4 min-w-max md:min-w-0 md:justify-center px-4 md:px-0'>
        {features.map((feature) => {
          const Icon = feature.icon;
          const isSelected = selectedFeature === feature.id;
          return (
            <button
              key={feature.id}
              onClick={() => {
                handleFeatureTabClick(feature.shortTitle);
                onSelectFeature(feature.id);
              }}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-full transition-all whitespace-nowrap ${
                isSelected
                  ? 'bg-primary text-white shadow-lg'
                  : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
              }`}
              style={{
                fontFamily:
                  "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                fontWeight: isSelected ? 600 : 400,
              }}
            >
              <Icon className='w-4 h-4' />
              {feature.shortTitle}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Feature Content Display
function FeatureContent({ featureId }: { featureId: string }) {
  const feature = features.find((f) => f.id === featureId) ?? features[0];
  const Icon = feature.icon;

  return (
    <div className='grid lg:grid-cols-2 gap-12 items-center'>
      {/* Image */}
      <div className='flex justify-center'>
        <img
          src={feature.image}
          alt={feature.title}
          className='w-full max-w-sm h-auto object-contain'
          style={{ maxHeight: '400px' }}
        />
      </div>

      {/* Content */}
      <div>
        <div className='w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-6'>
          <Icon className='w-7 h-7 text-primary' />
        </div>
        <h3
          className='text-3xl md:text-4xl font-bold text-gray-900 mb-4'
          style={{
            fontFamily:
              "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
            fontWeight: 700,
          }}
        >
          {feature.title}
        </h3>
        <p
          className='text-lg text-gray-600 mb-6 leading-relaxed'
          style={{
            fontFamily:
              "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
            fontWeight: 400,
          }}
        >
          {feature.description}
        </p>
        <ul className='space-y-3'>
          {feature.details.map((detail) => (
            <li key={detail} className='flex items-start gap-3'>
              <FiCheckCircle className='w-5 h-5 text-primary flex-shrink-0 mt-0.5' />
              <span
                className='text-gray-600'
                style={{
                  fontFamily:
                    "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                  fontWeight: 400,
                }}
              >
                {detail}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// Pricing Section
function PricingSection() {
  return (
    <section className='bg-white py-24'>
      <div className='max-w-4xl mx-auto px-6'>
        <div className='text-center mb-12'>
          <span
            className='text-primary font-semibold text-lg mb-2 block'
            style={{
              fontFamily:
                "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
              fontWeight: 600,
            }}
          >
            No hidden fees
          </span>
          <h2
            className='text-4xl md:text-5xl font-bold text-gray-900 mb-4'
            style={{
              fontFamily:
                "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
              fontWeight: 700,
            }}
          >
            Start Today
          </h2>
          <p
            className='text-xl text-gray-600'
            style={{
              fontFamily:
                "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
              fontWeight: 400,
            }}
          >
            Everything you need to co-parent effectively.
          </p>
        </div>

        {/* Affordability Comparison */}
        <div className='mb-8 flex justify-center'>
          <div className='bg-emerald-50/50 border border-emerald-200/50 rounded-lg px-4 py-2.5 flex items-center gap-3 max-w-xl'>
            <FiTrendingDown className='w-4 h-4 text-emerald-600 flex-shrink-0' />
            <p
              className='text-sm text-emerald-700'
              style={{
                fontFamily:
                  "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                fontWeight: 600,
              }}
            >
              <span className='font-bold'>60% less</span> than OurFamilyWizard &
              TalkingParents — save{' '}
              <span className='font-bold'>$180+ per year</span>
            </p>
          </div>
        </div>

        {/* Pricing Card */}
        <div className='border-2 border-primary bg-primary/5 rounded-2xl p-8 max-w-md mx-auto'>
          <div className='text-center mb-6'>
            <div
              className='text-4xl font-bold text-gray-900 mb-1'
              style={{
                fontFamily:
                  "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                fontWeight: 700,
              }}
            >
              $9.99
              <span
                className='text-lg font-normal text-gray-500'
                style={{ fontWeight: 400 }}
              >
                {' '}
                / month
              </span>
            </div>
            <div
              className='text-xl text-gray-600'
              style={{
                fontFamily:
                  "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                fontWeight: 400,
              }}
            >
              or $49.99 / year after 7-day trial
            </div>
          </div>

          <ul className='space-y-3 mb-8'>
            {getPricingFeatures('learn').map((feature: PricingFeature) => {
              const Icon = feature.icon;
              return (
                <li key={feature.id} className='flex items-center gap-3'>
                  <Icon className='w-4 h-4 text-primary flex-shrink-0' />
                  <span
                    className='text-gray-600'
                    style={{
                      fontFamily:
                        "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                      fontWeight: 400,
                    }}
                  >
                    {feature.text}
                  </span>
                </li>
              );
            })}
          </ul>

          {/* CTAs */}
          <div className='flex flex-col gap-3'>
            <a
              href={getAppStoreUrl(
                'learn-coparents-pricing',
                getUtmParamsFromUrl()
              )}
              target='_blank'
              rel='noopener noreferrer'
              onClick={() => handleAppStoreClick('Learn Coparents Pricing')}
              className='flex items-center justify-center gap-2 w-full bg-primary hover:bg-primary-hover text-white font-semibold py-3 rounded-lg transition-colors'
              style={{
                fontFamily:
                  "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                fontWeight: 600,
              }}
            >
              <FaApple size={20} />
              <span>App Store</span>
            </a>
            <a
              href={getPlayStoreUrl(
                'learn-coparents-pricing',
                getUtmParamsFromUrl()
              )}
              target='_blank'
              rel='noopener noreferrer'
              onClick={() => handleGooglePlayClick('Learn Coparents Pricing')}
              className='flex items-center justify-center gap-2 w-full bg-primary hover:bg-primary-hover text-white font-semibold py-3 rounded-lg transition-colors'
              style={{
                fontFamily:
                  "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                fontWeight: 600,
              }}
            >
              <FaGooglePlay size={20} />
              <span>Google Play</span>
            </a>
            <Link
              to='/auth/sign-up'
              onClick={() => handleSignUpClick('Learn Coparents Pricing')}
              className='flex items-center justify-center gap-2 w-full border-2 border-primary text-primary hover:bg-primary/5 font-semibold py-3 rounded-lg transition-colors'
              style={{
                fontFamily:
                  "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                fontWeight: 600,
              }}
            >
              Sign Up on Web
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

// Contact Section
function ContactSection() {
  // Fetch support info
  const supportQuery = useQuery({
    queryKey: ['support-info'],
    queryFn: () => fetchApi<SupportInfoResponse>('/v1/support-info'),
    staleTime: 1000 * 60 * 60, // 1 hour
  });

  const supportPhone = supportQuery.data?.phone_number ?? '+14157469059';

  return (
    <section className='bg-primary py-20'>
      <div className='max-w-4xl mx-auto px-6'>
        <div className='text-center mb-12'>
          <h2
            className='text-3xl md:text-4xl font-bold text-white mb-4'
            style={{
              fontFamily:
                "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
              fontWeight: 700,
            }}
          >
            Questions? We're Here to Help
          </h2>
          <p
            className='text-xl text-white/80'
            style={{
              fontFamily:
                "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
              fontWeight: 400,
            }}
          >
            Reach out anytime — we're always happy to help.
          </p>
        </div>

        <div className='grid md:grid-cols-2 gap-6'>
          {/* Email Contact */}
          <a
            href='mailto:team@harborcoparent.com'
            onClick={() => handleContactEmailClick('Learn Coparents')}
            className='bg-white/10 hover:bg-white/20 rounded-2xl p-6 transition-colors group'
          >
            <div className='w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-white/30 transition-colors'>
              <FiMail className='w-6 h-6 text-white' />
            </div>
            <h3
              className='text-xl font-bold text-white mb-2'
              style={{
                fontFamily:
                  "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                fontWeight: 700,
              }}
            >
              Email Us
            </h3>
            <p
              className='text-white/80 mb-3'
              style={{
                fontFamily:
                  "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                fontWeight: 400,
              }}
            >
              Send us a message and we'll get back to you within 24 hours.
            </p>
            <span className='text-white font-medium'>
              team@harborcoparent.com →
            </span>
          </a>

          {/* SMS Support */}
          <a
            href={`sms:${supportPhone}`}
            onClick={() => handleSupportSMSClick('Learn Coparents')}
            className='bg-white/10 hover:bg-white/20 rounded-2xl p-6 transition-colors group'
          >
            <div className='w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-white/30 transition-colors'>
              <FiMessageSquare className='w-6 h-6 text-white' />
            </div>
            <h3
              className='text-xl font-bold text-white mb-2'
              style={{
                fontFamily:
                  "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                fontWeight: 700,
              }}
            >
              Text Support
            </h3>
            <p
              className='text-white/80 mb-3'
              style={{
                fontFamily:
                  "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                fontWeight: 400,
              }}
            >
              Support available 24/7 — text us anytime.
            </p>
            <span className='text-white font-medium'>
              Text {formatPhoneNumber(supportPhone)} →
            </span>
          </a>
        </div>
      </div>
    </section>
  );
}

// Main Page Component
function LearnCoparentsPage() {
  const [selectedFeature, setSelectedFeature] = useState('expenses');
  const divorceEnabled = useIsDivorceProductEnabled();

  useEffect(() => {
    track('Learn Coparents Screen');
  }, []);

  return (
    <div className='min-h-screen bg-white'>
      <SEO
        title='Harbor for Co-Parents | Expense Tracking, Messaging & Scheduling'
        description={HARBOR_FACTS.quote}
        path='/learn/coparents'
        jsonLd={buildHarborSoftwareApplicationJsonLd()}
      />
      <LearnHeader />

      {/* Hero Section */}
      <section className='bg-primary/5 pt-32 pb-8'>
        <div className='max-w-4xl mx-auto px-6 text-center'>
          <h1
            className='text-4xl md:text-5xl font-bold text-gray-900 mb-4'
            style={{
              fontFamily:
                "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
              fontWeight: 700,
            }}
          >
            Harbor for Co-Parents
          </h1>
          <p
            className='text-xl text-gray-600 max-w-2xl mx-auto'
            style={{
              fontFamily:
                "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
              fontWeight: 400,
            }}
          >
            Everything you need to manage expenses, schedules, and communication
            with your co-parent — all in one secure app. Start solo or invite both
            parents; Harbor works either way.
          </p>
          {divorceEnabled ? (
            <p
              className='mt-4 text-gray-600'
              style={{
                fontFamily:
                  "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                fontWeight: 400,
              }}
            >
              Still working through the divorce itself?{' '}
              <Link to='/divorce' className='font-semibold text-primary hover:underline'>
                See the free divorce checklist for your state
              </Link>
              .
            </p>
          ) : null}
        </div>
      </section>

      <AtAGlance />

      <section className='bg-white pb-8'>
        <div className='mx-auto grid max-w-5xl gap-6 px-6 md:grid-cols-2'>
          <article className='rounded-xl border border-gray-200 bg-gray-50 p-6'>
            <h2
              className='text-xl font-bold text-gray-900'
              style={{
                fontFamily:
                  "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                fontWeight: 700,
              }}
            >
              Works if only one parent joins
            </h2>
            <p
              className='mt-3 leading-relaxed text-gray-700'
              style={{
                fontFamily:
                  "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                fontWeight: 400,
              }}
            >
              Start Harbor solo. Log expenses, keep a custody calendar, and build a
              record before the other parent accepts an invite. You do not need both
              parents onboarded on day one.
            </p>
          </article>
          <article className='rounded-xl border border-gray-200 bg-gray-50 p-6'>
            <h2
              className='text-xl font-bold text-gray-900'
              style={{
                fontFamily:
                  "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                fontWeight: 700,
              }}
            >
              Temperature Check vs ToneMeter
            </h2>
            <p
              className='mt-3 leading-relaxed text-gray-700'
              style={{
                fontFamily:
                  "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                fontWeight: 400,
              }}
            >
              Harbor Temperature Check scores a draft in conversation context and
              offers a calmer rewrite before you send. It is included in the
              paid plan. OurFamilyWizard ToneMeter is the closest incumbent
              feature and usually sits on higher paid tiers.
            </p>
          </article>
        </div>
      </section>

      {/* Features Section */}
      <section className='py-16 bg-sky-light'>
        <div className='max-w-6xl mx-auto px-6'>
          <div className='text-center mb-12'>
            <span
              className='text-primary font-semibold text-lg mb-2 block'
              style={{
                fontFamily:
                  "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                fontWeight: 600,
              }}
            >
              Features
            </span>
            <h2
              className='text-3xl md:text-4xl font-bold text-gray-900 mb-8'
              style={{
                fontFamily:
                  "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                fontWeight: 700,
              }}
            >
              Everything You Need
            </h2>
            <FeatureTabCarousel
              selectedFeature={selectedFeature}
              onSelectFeature={setSelectedFeature}
            />
          </div>

          <div className='mt-12'>
            <FeatureContent featureId={selectedFeature} />
          </div>
        </div>
      </section>

      <PricingSection />
      <ContactSection />
      <Footer />
    </div>
  );
}

export const Route = createFileRoute('/learn/coparents')({
  component: LearnCoparentsPage,
});
